import { Alert, Button } from '@catering/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { JSX } from 'react';
import { useState } from 'react';

import { hasPermission } from '../../../auth/has-permission';
import { useCurrentUser } from '../../../auth/use-current-user';
import type { DietaryTag, MenuDetail } from '../../../menus/api';
import { updateMenu } from '../../../menus/api';
import { mapMenuPatchError } from '../menu-form-schema';

export interface MenuDietaryTagsTabProps {
  menu: MenuDetail;
  dietaryTags: DietaryTag[];
}

/**
 * `menus/spec.md` → "Asignación de etiquetas dietarias", "Validación de
 * coherencia dietaria". The assigned list always comes from server data
 * (`menu.dietaryTags`), never from local state: a rejected PATCH must leave
 * the screen showing exactly what the server still has.
 */
export function MenuDietaryTagsTab({ menu, dietaryTags }: MenuDietaryTagsTabProps): JSX.Element {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState('');
  const currentUser = useCurrentUser();
  const readOnly = menu.deletedAt !== null || !hasPermission(currentUser.data, 'menu.update');

  const mutation = useMutation({
    mutationFn: (dietaryTagIds: string[]) => updateMenu(menu.id, { dietaryTagIds }),
    onSuccess: async () => {
      setSelected('');
      await queryClient.invalidateQueries({ queryKey: ['menu', menu.id] });
    },
  });

  const assignedIds = new Set(menu.dietaryTags.map((tag) => tag.id));
  // Inactive tags never appear as an option, whether or not they are already
  // assigned to the menu (`design.md` §6): only the active catalog is
  // offered for a new assignment.
  const availableOptions = dietaryTags.filter((tag) => tag.isActive && !assignedIds.has(tag.id));

  function addTag(): void {
    if (selected === '') {
      return;
    }

    mutation.mutate([...assignedIds, selected]);
  }

  function removeTag(tagId: string): void {
    mutation.mutate([...assignedIds].filter((id) => id !== tagId));
  }

  return (
    <div className="flex flex-col gap-4">
      {mutation.isError ? <Alert tone="danger">{mapMenuPatchError(mutation.error)}</Alert> : null}

      {menu.dietaryTags.length === 0 ? (
        <p className="text-sm text-slate-500">Este menú no tiene etiquetas dietarias asignadas.</p>
      ) : (
        <ul className="flex flex-wrap gap-2">
          {menu.dietaryTags.map((tag) => (
            <li
              key={tag.id}
              className="flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-sm text-slate-900"
            >
              {tag.name}
              {!readOnly ? (
                <button
                  type="button"
                  aria-label={`Quitar ${tag.name}`}
                  onClick={() => removeTag(tag.id)}
                  className="text-slate-500 hover:text-slate-900"
                >
                  ×
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {!readOnly ? (
        <div className="flex items-end gap-3">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="menu-dietary-tag-select" className="text-sm font-medium text-slate-900">
              Agregar etiqueta
            </label>
            <select
              id="menu-dietary-tag-select"
              value={selected}
              onChange={(event) => setSelected(event.target.value)}
              className="h-11 rounded-md border border-slate-300 px-3 text-sm"
            >
              <option value="">Seleccioná una etiqueta</option>
              {availableOptions.map((tag) => (
                <option key={tag.id} value={tag.id}>
                  {tag.name}
                </option>
              ))}
            </select>
          </div>
          <Button
            type="button"
            loading={mutation.isPending}
            disabled={selected === ''}
            onClick={addTag}
          >
            Agregar
          </Button>
        </div>
      ) : null}
    </div>
  );
}
